/**
 * @fileoverview Reusable cost input with currency selector.
 * Handles decimal formatting, comma/dot separator, and currency selection.
 * @module shared/components/ui/CostInput
 */

import { type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { Label } from '@/components/ui/label'
import {
    InputGroup,
    InputGroupInput,
    InputGroupAddon,
} from '@/components/ui/input-group'
import { CurrencySelect } from './CurrencySelect'
import { cn } from '@/lib/utils'

/**
 * Zod schema for cost string validation.
 * Accepts empty string, or valid decimal number format.
 * Normalizes comma to dot internally.
 */
export const costStringSchema = z
    .string()
    .optional()
    .transform((val) => val?.replace(',', '.'))
    .refine(
        (val) => !val || val === '' || !isNaN(parseFloat(val)),
        { message: 'Must be a valid number' }
    )

/**
 * Parse cost string to number for API submission.
 * @param value - Cost string value from form
 * @returns Number or null if empty/invalid
 */
export function parseCostToNumber(value: string | undefined): number | null {
    if (!value || value === '') return null
    const normalized = value.replace(',', '.')
    const num = parseFloat(normalized)
    return isNaN(num) ? null : num
}

/**
 * Format number to cost string with 2 decimal places.
 * @param value - Number value from API
 * @returns Formatted string or empty string
 */
export function formatCostFromNumber(value: number | null | undefined): string {
    if (value == null) return ''
    return value.toFixed(2)
}

export interface CostInputProps {
    /** Label text (defaults to translated "Cost") */
    label?: string
    /** Cost value (string for controlled input) */
    costValue: string
    /** Cost change handler */
    onCostChange: (value: string) => void
    /** Cost blur handler (for formatting) */
    onCostBlur?: () => void
    /** Key down handler on the cost input (e.g. submit on Enter) */
    onCostKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
    /** Currency value */
    currencyValue: string | undefined
    /** Currency change handler */
    onCurrencyChange: (value: string) => void
    /** Placeholder for cost input */
    placeholder?: string
    /** Whether the input is disabled */
    disabled?: boolean
    /** Additional class name for the container */
    className?: string
    /** Hide label */
    hideLabel?: boolean
}

/**
 * Cost input with integrated currency selector.
 * Handles decimal formatting and comma/dot normalization.
 *
 * @component
 * @example
 * ```tsx
 * <CostInput
 *   label="Cost"
 *   costValue={form.watch('originalCost')}
 *   onCostChange={(val) => form.setValue('originalCost', val)}
 *   currencyValue={form.watch('originalCurrency')}
 *   onCurrencyChange={(val) => form.setValue('originalCurrency', val)}
 * />
 * ```
 */
export function CostInput({
    label,
    costValue,
    onCostChange,
    onCostBlur,
    onCostKeyDown,
    currencyValue,
    onCurrencyChange,
    placeholder = '0.00',
    disabled = false,
    className,
    hideLabel = false,
}: CostInputProps): ReactElement {
    const { t } = useTranslation('common')
    const displayLabel = label ?? t('form.cost')

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        // Normalize comma to dot as user types
        const value = e.target.value.replace(',', '.')
        onCostChange(value)
    }

    const handleBlur = (): void => {
        onCostBlur?.()
        // Format to 2 decimals on blur
        const normalized = costValue.replace(',', '.')
        const val = parseFloat(normalized)
        if (!isNaN(val)) {
            onCostChange(val.toFixed(2))
        }
    }

    return (
        <div className={cn('space-y-2', className)}>
            {!hideLabel && <Label>{displayLabel}</Label>}
            <InputGroup>
                <InputGroupInput
                    type="text"
                    inputMode="decimal"
                    placeholder={placeholder}
                    className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    value={costValue}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onKeyDown={onCostKeyDown}
                    disabled={disabled}
                />
                <InputGroupAddon align="inline-end" className="border-l-2 border-input">
                    <CurrencySelect
                        value={currencyValue}
                        onValueChange={onCurrencyChange}
                        placeholder={t('form.currency')}
                        disabled={disabled}
                        compact
                    />
                </InputGroupAddon>
            </InputGroup>
        </div>
    )
}

export default CostInput
